import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import EditCloverPointConfigContext from 'contexts/EditCloverPointConfigContext';
import { getCloverPointConfigs } from 'services/cloverServices';
import CloverPointConfig from 'types/models/CloverPointConfig';

export default (): JSX.Element => {
  const history = useHistory();
  const [paginationCloverConfig, setPaginationCloverConfigs] = useState<PaginationData<CloverPointConfig> | null>(null);
  const { setSelectedCloverPointConfig } = React.useContext(EditCloverPointConfigContext);
  const { t } = useTranslation();

  const goToPage = (url: string): void => {
    history.push(url);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = React.useCallback(async (page?: number) => {
    const paginationData = await getCloverPointConfigs(page);
    if (paginationData) setPaginationCloverConfigs(paginationData);
  }, []);

  const renderEditButton = (cloverPointConfig: CloverPointConfig): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedCloverPointConfig(cloverPointConfig);
          history.push('clover-point/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: CloverPointConfig[]): JSX.Element => {
    return (
      <>
        {paginationData.map((cloverConfig: CloverPointConfig) => (
          <TableRow key={cloverConfig.description}>
            <TableCell component="th" scope="row">
              {cloverConfig.description}
            </TableCell>
            <TableCell component="th" scope="row">
              {cloverConfig.friendlyId}
            </TableCell>
            <TableCell>{renderEditButton(cloverConfig)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="primary" onClick={() => goToPage('clover-point/create')}>
          {t('listCloverPointConfigs-page-create')}
        </Button>
      </div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="secondary" onClick={() => history.go(-1)}>
          {t('go-back')}
        </Button>
      </div>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listCloverPointConfigs-page-description')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-friendly-id')}</TableCell>
              <TableCell>{t('listCloverPointConfigs-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationCloverConfig?.data && paginationCloverConfig?.data.length > 0 ? (
              renderContent(paginationCloverConfig.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listCloverPointConfigs-page-noConfigs')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationCloverConfig && (
        <TablePagination
          component="div"
          count={paginationCloverConfig.total}
          page={paginationCloverConfig.currentPage - 1}
          onPageChange={(_, page: number) => {
            loadData(page + 1);
          }}
          rowsPerPage={paginationCloverConfig.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
};
